import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatRelative, MOVEMENT_LABELS } from '../lib/format';
import type { MovementEntry } from '../lib/queries';
import { BarRow, ErrorNote, PageHeader, Spinner, Stat } from '../components/ui';

interface Dashboard {
  totals: {
    assets: number;
    archived: number;
    in_stock: number;
    deployed: number;
    unassigned: number;
    employees_with_assets: number;
    pending_confirmations: number;
  };
  by_category: { name: string; count: number }[];
  by_status: { name: string; count: number }[];
  by_location: { name: string; count: number }[];
  oldest: {
    id: number;
    asset_tag: string;
    model_name: string | null;
    purchase_date: string | null;
  }[];
  recent_movements: MovementEntry[];
}

export function DashboardPage() {
  const auth = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<Dashboard>('/dashboard'),
  });

  if (isLoading) return <Spinner label="Bestand wird geladen …" />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const maxCategory = Math.max(...data.by_category.map((c) => c.count), 1);
  const maxLocation = Math.max(...data.by_location.map((c) => c.count), 1);

  return (
    <div>
      <PageHeader
        eyebrow={greeting()}
        title={auth.user?.name.split(' ')[0] ?? 'Übersicht'}
        subtitle="Was gerade im Bestand ist und was sich zuletzt bewegt hat."
      />

      <div className="stagger mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Im Bestand" value={data.totals.assets} hint="ohne ausgemusterte" to="/geraete" />
        <Stat
          label="Ausgegeben"
          value={data.totals.deployed}
          hint="an Personen und Standorte"
          to="/geraete"
        />
        <Stat label="Im Lager" value={data.totals.in_stock} hint="verfügbar" to="/geraete" />
        <Stat
          label="Ausgemustert"
          value={data.totals.archived}
          hint="verschrottet oder verkauft"
        />
      </div>

      {data.totals.pending_confirmations > 0 && (
        <div className="surface animate-fade-up mb-8 flex flex-wrap items-center justify-between gap-3 border-[var(--color-khadi-saffron-400)] bg-[var(--color-khadi-saffron-100)] px-5 py-4">
          <p className="text-sm text-[var(--color-fg1)]">
            <strong className="tabular">{data.totals.pending_confirmations}</strong> Übergaben
            warten noch auf die Bestätigung der Empfänger.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="surface pad">
          <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">Nach Kategorie</h2>
          <div className="space-y-3">
            {data.by_category.map((entry) => (
              <BarRow
                key={entry.name}
                label={entry.name}
                value={entry.count}
                max={maxCategory}
                tone="forest"
              />
            ))}
          </div>
        </section>

        <section className="surface pad">
          <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">Nach Standort</h2>
          <div className="space-y-3">
            {data.by_location.map((entry) => (
              <BarRow
                key={entry.name}
                label={entry.name}
                value={entry.count}
                max={maxLocation}
                tone="clay"
              />
            ))}
          </div>
        </section>

        <section className="surface pad">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="font-[family-name:var(--font-display)] text-2xl">Zuletzt bewegt</h2>
            <Link to="/geraete" className="text-sm text-[var(--color-fg3)] no-underline hover:text-[var(--color-accent)]">
              alle
            </Link>
          </div>

          <ul className="space-y-2.5">
            {data.recent_movements.slice(0, 8).map((movement) => (
              <li key={movement.id}>
                <Link
                  to="/geraete/$assetId"
                  params={{ assetId: String(movement.asset_id) }}
                  className="group flex items-start gap-2.5 no-underline"
                >
                  <span
                    aria-hidden
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-khadi-forest-400)] transition-colors group-hover:bg-[var(--color-accent)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-[var(--color-fg1)]">
                      <span className="tabular font-semibold">{movement.asset?.asset_tag}</span>{' '}
                      {MOVEMENT_LABELS[movement.kind] ?? movement.kind}
                      {movement.to?.name ? ` an ${movement.to.name}` : ''}
                    </span>
                    <span className="block text-sm text-[var(--color-fg3)]">
                      {formatRelative(movement.occurred_at)}
                      {movement.actor ? ` · ${movement.actor}` : ''}
                    </span>
                  </span>
                  <ArrowRight
                    className="mt-1 h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {data.oldest.length > 0 && (
        <section className="surface mt-4 p-5">
          <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">
            Älteste Geräte im Einsatz
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.oldest.map((asset) => (
              <Link
                key={asset.id}
                to="/geraete/$assetId"
                params={{ assetId: String(asset.id) }}
                className="card-link surface-quiet px-5 py-4 text-sm no-underline"
              >
                <span className="tabular font-semibold">{asset.asset_tag}</span>
                <span className="ml-2 text-[var(--color-fg3)]">{asset.model_name}</span>
                <span className="ml-2 text-sm text-[var(--color-fg3)]">
                  {asset.purchase_date?.slice(0, 4)}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}
